import React, { useState, useEffect, useCallback } from 'react';
import { getAllApplicants, updateApplicantStatus } from '../utils/applicants';
import AdminLayout from '../layouts/AdminLayout';
import { FiSearch, FiX, FiCheck, FiMail, FiUser, FiBriefcase, FiDownload } from 'react-icons/fi';

const Applicants = () => {
  const [applicants, setApplicants] = useState([]);
  const [filteredApplicants, setFilteredApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    field: '',
    gender: '',
    status: ''
  });
  const [message, setMessage] = useState({});
  const [selectedApplicant, setSelectedApplicant] = useState(null);

  const fetchApplicants = useCallback(async () => {
    try {
      setLoading(true);

      const result = await getAllApplicants(filters);
      if (result.success) {
        setApplicants(result.data);
        setFilteredApplicants(result.data);
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (err) {
      console.error('Error fetching applicants:', err);
      setMessage({ type: 'error', text: 'حدث خطأ في جلب بيانات المتقدمين' });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchApplicants();
  }, [fetchApplicants]);

  useEffect(() => {
    const filtered = applicants.filter(applicant =>
      applicant.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      applicant.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredApplicants(filtered);
  }, [searchTerm, applicants]);

  const handleStatusUpdate = async (applicantId, email, newStatus) => {
    try {
      const result = await updateApplicantStatus(applicantId, email, newStatus);
      setMessage({ type: result.success ? 'success' : 'error', text: result.message });

      if (result.success) {
        setApplicants(prev =>
          prev.map(applicant =>
            applicant.id === applicantId
              ? { ...applicant, status: newStatus }
              : applicant
          )
        );
      }
      setTimeout(() => setMessage({}), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setMessage({ type: 'error', text: 'حدث خطأ أثناء تحديث الحالة' });
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      field: '',
      gender: '',
      status: ''
    });
    setSearchTerm('');
  };

  const exportToCSV = () => {
    // Prepare CSV data with proper structure
    const csvHeaders = ['"FIRSTNAME"', '"LASTNAME"', '"EMAIL"', '"NSAVE"', '"SMS"'];

    const csvRows = filteredApplicants.map(applicant => {
      // Split full name into first and last name
      const nameParts = applicant.full_name?.trim().split(' ') || ['', ''];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      return [
        `"${firstName}"`,
        `"${lastName}"`,
        `"${applicant.email || ''}"`,
        `"${applicant.nsave_id || ''}"`,
        `"${applicant.referral || ''}"`
      ].join(',');
    });

    const csvContent = [csvHeaders.join(','), ...csvRows].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qimma-applicants-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'مقبول':
        return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500' };
      case 'مرفوض':
        return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500' };
      default:
        return { bg: 'bg-yellow/20', text: 'text-yellow', border: 'border-yellow' };
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow mx-auto mb-4"></div>
            <p className="text-yellow">جاري التحميل...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-yellow mb-2">
            إدارة <span className="text-white">المتقدمين</span>
          </h1>
          <p className="text-white/70">مراجعة وموافقة طلبات التقديم</p>
        </div>
        <button
          onClick={exportToCSV}
          className="flex items-center gap-2 px-6 py-3 bg-yellow text-[#05339c] rounded-xl hover:bg-yellow/80 transition-all duration-300 font-semibold"
        >
          <FiDownload size={20} />
          <span>تصدير CSV</span>
        </button>
      </div>

      {/* Success/Error Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between border-2 ${message.type === 'success'
            ? 'bg-green-500/20 border-green-500 text-green-400'
            : 'bg-red-500/20 border-red-500 text-red-400'
          }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <FiCheck /> : <FiX />}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage({})} className="text-white/70 hover:text-white">
            <FiX />
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <div className="relative">
              <FiSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50" size={20} />
              <input
dir="ltr"                type="text"
                placeholder="البحث بالاسم أو البريد الإلكتروني..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-3 bg-blue border-2 border-white/20 text-white placeholder-white/50 rounded-lg focus:outline-none focus:border-yellow"
              />
            </div>
          </div>

          {/* Field Filter */}
          <select
            value={filters.field}
            onChange={(e) => handleFilterChange('field', e.target.value)}
            className="px-4 py-3 bg-blue border-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-yellow"
          >
            <option value="">جميع المجالات</option>
            <option value="تطوير الويب">تطوير الويب</option>
            <option value="تطوير التطبيقات">تطوير التطبيقات</option>
            <option value="الذكاء الاصطناعي">الذكاء الاصطناعي</option>
            <option value="الأمن السيبراني">الأمن السيبراني</option>
            <option value="تحليل البيانات">تحليل البيانات</option>
            <option value="أخرى">أخرى</option>
          </select>

          {/* Gender Filter */}
          <select
            value={filters.gender}
            onChange={(e) => handleFilterChange('gender', e.target.value)}
            className="px-4 py-3 bg-blue border-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-yellow"
          >
            <option value="">جميع الأجناس</option>
            <option value="ذكر">ذكر</option>
            <option value="أنثى">أنثى</option>
          </select>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="px-4 py-3 bg-blue border-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-yellow"
          >
            <option value="">جميع الحالات</option>
            <option value="قيد المراجعة">قيد المراجعة</option>
            <option value="مقبول">مقبول</option>
            <option value="مرفوض">مرفوض</option>
          </select>
        </div>

        {/* Clear Filters */}
        {(filters.field || filters.gender || filters.status || searchTerm) && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-white/70">
              عرض {filteredApplicants.length} من {applicants.length} متقدم
            </p>
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <FiX size={16} />
              <span>مسح الفلاتر</span>
            </button>
          </div>
        )}
      </div>

      {/* Applicants Table */}
      <div className="bg-dark-blue border-2 border-yellow rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/10">
            <thead className="bg-blue">
              <tr>
                <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                  المتقدم
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                  المجال
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                  الإحالة
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                  الحالة
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-yellow uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filteredApplicants.map((applicant) => {
                const statusColors = getStatusColor(applicant.status);
                return (
                  <tr key={applicant.id} className="hover:bg-blue/20 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow rounded-full flex items-center justify-center text-[#05339c] font-semibold">
                          {applicant.full_name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-semibold text-white">{applicant.full_name}</div>
                          <div className="text-sm text-white/60 flex items-center gap-1">
                            <FiMail size={12} />
                            {applicant.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <FiBriefcase className="text-white/50" size={16} />
                        <span className="text-sm text-white">{applicant.field}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {applicant.referral ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow/20 text-yellow border-2 border-yellow">
                          {applicant.referral}
                        </span>
                      ) : (
                        <span className="text-sm text-white/40">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border-2 ${statusColors.bg} ${statusColors.text} ${statusColors.border}`}>
                        {applicant.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {applicant.status !== 'مقبول' && (
                          <button
                            onClick={() => handleStatusUpdate(applicant.id, applicant.email, 'مقبول')}
                            className="flex items-center gap-1 px-3 py-2 bg-green-500/20 text-green-400 border-2 border-green-500 rounded-lg hover:bg-green-500/30 transition-colors text-sm font-medium"
                          >
                            <FiCheck size={16} />
                            <span>قبول</span>
                          </button>
                        )}
                        {applicant.status !== 'مرفوض' && (
                          <button
                            onClick={() => handleStatusUpdate(applicant.id, applicant.email, 'مرفوض')}
                            className="flex items-center gap-1 px-3 py-2 bg-red-500/20 text-red-400 border-2 border-red-500 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-medium"
                          >
                            <FiX size={16} />
                            <span>رفض</span>
                          </button>
                        )}
                        <button
                          onClick={() => setSelectedApplicant(applicant)}
                          className="px-3 py-2 bg-blue text-yellow border-2 border-yellow rounded-lg hover:bg-blue/80 transition-colors text-sm font-medium"
                        >
                          تفاصيل
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredApplicants.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiSearch className="text-white/50" size={32} />
            </div>
            <p className="text-white mb-2">لا توجد نتائج</p>
            <p className="text-white/60 text-sm">جرب تغيير الفلاتر أو البحث</p>
          </div>
        )}
      </div>

      {/* Applicant Details Modal */}
      {selectedApplicant && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-yellow px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#05339c]">تفاصيل المتقدم</h3>
              <button
                onClick={() => setSelectedApplicant(null)}
                className="text-[#05339c] hover:bg-[#05339c] hover:text-yellow p-2 rounded-lg transition-all"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-yellow flex items-center gap-2">
                  <FiUser />
                  المعلومات الشخصية
                </h4>
                <div className="grid grid-cols-2 gap-4 bg-blue rounded-xl p-4 border-2 border-white/10">
                  <div>
                    <p className="text-xs text-white/50 mb-1">الاسم الكامل</p>
                    <p className="font-medium text-white">{selectedApplicant.full_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 mb-1">البريد الإلكتروني</p>
                    <p className="font-medium text-white text-sm">{selectedApplicant.email}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 mb-1">العمر</p>
                    <p className="font-medium text-white">{selectedApplicant.age} سنة</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 mb-1">الجنس</p>
                    <p className="font-medium text-white">{selectedApplicant.gender}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 mb-1">معرف NSAVE</p>
                    <p className="font-medium text-white">{selectedApplicant.nsave_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 mb-1">الحالة</p>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border-2 ${getStatusColor(selectedApplicant.status).bg} ${getStatusColor(selectedApplicant.status).text} ${getStatusColor(selectedApplicant.status).border}`}>
                      {selectedApplicant.status}
                    </span>
                  </div>
                  {selectedApplicant.referral && (
                    <div className="col-span-2">
                      <p className="text-xs text-white/50 mb-1">كود الإحالة</p>
                      <p className="font-medium text-yellow">{selectedApplicant.referral}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Professional Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-yellow flex items-center gap-2">
                  <FiBriefcase />
                  المعلومات المهنية
                </h4>
                <div className="bg-blue rounded-xl p-4 space-y-3 border-2 border-white/10">
                  <div>
                    <p className="text-xs text-white/50 mb-1">المجال</p>
                    <p className="font-medium text-white">{selectedApplicant.field}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50 mb-2">المهارات</p>
                    <p className="text-sm text-white/80">{selectedApplicant.skills}</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                {selectedApplicant.status !== 'مقبول' && (
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedApplicant.id, selectedApplicant.email, 'مقبول');
                      setSelectedApplicant(null);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-semibold"
                  >
                    <FiCheck size={20} />
                    <span>قبول المتقدم</span>
                  </button>
                )}
                {selectedApplicant.status !== 'مرفوض' && (
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedApplicant.id, selectedApplicant.email, 'مرفوض');
                      setSelectedApplicant(null);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-semibold"
                  >
                    <FiX size={20} />
                    <span>رفض المتقدم</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Applicants;
